/**
 * Patient Form API Service
 * Handles all API calls related to patient forms
 */

import axios from 'axios';
import type { PatientFormTemplate, PatientFormSubmission } from '@/types/patientForm';

const API_BASE_URL = '/api/v1/pathology';

// Get auth token from localStorage
const getAuthToken = () => localStorage.getItem('authToken');

// Create axios instance with auth
const createAuthAxios = () => {
  const token = getAuthToken();
  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Authorization': token ? `Token ${token}` : '',
      'Content-Type': 'application/json',
    },
  });
};

export const patientFormApi = {
  // Template endpoints
  async getTemplates(): Promise<PatientFormTemplate[]> {
    const api = createAuthAxios();
    const response = await api.get('/patient-form-templates/');
    return response.data;
  },

  async getTemplate(id: number): Promise<PatientFormTemplate> {
    const api = createAuthAxios();
    const response = await api.get(`/patient-form-templates/${id}/`);
    return response.data;
  },

  async getDefaultTemplate(): Promise<PatientFormTemplate> {
    const api = createAuthAxios();
    const response = await api.get('/patient-form-templates/get_default/');
    return response.data;
  },

  async createTemplate(data: Partial<PatientFormTemplate>): Promise<PatientFormTemplate> {
    const api = createAuthAxios();
    const response = await api.post('/patient-form-templates/', data);
    return response.data;
  },

  async updateTemplate(id: number, data: Partial<PatientFormTemplate>): Promise<PatientFormTemplate> {
    const api = createAuthAxios();
    const response = await api.put(`/patient-form-templates/${id}/`, data);
    return response.data;
  },

  async patchTemplate(id: number, data: Partial<PatientFormTemplate>): Promise<PatientFormTemplate> {
    const api = createAuthAxios();
    const response = await api.patch(`/patient-form-templates/${id}/`, data);
    return response.data;
  },

  async deleteTemplate(id: number): Promise<void> {
    const api = createAuthAxios();
    await api.delete(`/patient-form-templates/${id}/`);
  },

  async setDefaultTemplate(id: number): Promise<PatientFormTemplate> {
    const api = createAuthAxios();
    const response = await api.post(`/patient-form-templates/${id}/set_default/`);
    return response.data;
  },

  // Submission endpoints
  async getSubmissions(patientId?: number): Promise<PatientFormSubmission[]> {
    const api = createAuthAxios();
    const params = patientId ? { patient: patientId } : {};
    const response = await api.get('/patient-form-submissions/', { params });
    return response.data;
  },

  async getSubmission(id: number): Promise<PatientFormSubmission> {
    const api = createAuthAxios();
    const response = await api.get(`/patient-form-submissions/${id}/`);
    return response.data;
  },

  async createSubmission(data: {
    patient_id: number;
    template_id?: number;
    form_data: Record<string, any>;
  }): Promise<PatientFormSubmission> {
    const api = createAuthAxios();
    const response = await api.post('/patient-form-submissions/', data);
    return response.data;
  },

  async updateSubmission(
    id: number,
    data: Partial<PatientFormSubmission>
  ): Promise<PatientFormSubmission> {
    const api = createAuthAxios();
    const response = await api.put(`/patient-form-submissions/${id}/`, data);
    return response.data;
  },

  async deleteSubmission(id: number): Promise<void> {
    const api = createAuthAxios();
    await api.delete(`/patient-form-submissions/${id}/`);
  },
};
